import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useContentModeration, ContentReport, ContentType, ReportStatus } from '@/hooks/useContentModeration';
import { ModerationStats } from '@/components/admin/ModerationStats';
import { ReportFilters } from '@/components/admin/ReportFilters';
import { ReportCard } from '@/components/admin/ReportCard';
import { ModerationActionDialog } from '@/components/admin/ModerationActionDialog';
import { TagModerationPanel } from '@/components/admin/TagModerationPanel';
import { AICostTracker } from '@/components/admin/AICostTracker';
import { AlertCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function AdminModeration() {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const {
    reports,
    loading,
    stats,
    fetchReports,
    dismissReport,
    markAsActioned,
  } = useContentModeration();

  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'all'>('all');
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentType | 'all'>('all');
  const [selectedReport, setSelectedReport] = useState<ContentReport | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Redirect if not admin
  if (!adminLoading && !isAdmin) {
    navigate('/');
    return null;
  }

  if (adminLoading || loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="grid grid-cols-3 gap-4">
            <div className="h-24 bg-muted rounded" />
            <div className="h-24 bg-muted rounded" />
            <div className="h-24 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  const handleStatusChange = (status: ReportStatus | 'all') => {
    setStatusFilter(status);
    fetchReports({
      status: status === 'all' ? undefined : status,
      contentType: contentTypeFilter === 'all' ? undefined : contentTypeFilter,
    });
  };

  const handleContentTypeChange = (type: ContentType | 'all') => {
    setContentTypeFilter(type);
    fetchReports({
      status: statusFilter === 'all' ? undefined : statusFilter,
      contentType: type === 'all' ? undefined : type,
    });
  };

  const handleViewDetails = (report: ContentReport) => {
    setSelectedReport(report);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    fetchReports({
      status: statusFilter === 'all' ? undefined : statusFilter,
      contentType: contentTypeFilter === 'all' ? undefined : contentTypeFilter,
    });
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Content Moderation Dashboard</h1>
        <p className="text-muted-foreground">
          Review and manage reported content from users
        </p>
      </div>

      <Tabs defaultValue="reports" className="space-y-6">
        <TabsList>
          <TabsTrigger value="reports">Content Reports</TabsTrigger>
          <TabsTrigger value="tags">Tag Moderation</TabsTrigger>
          <TabsTrigger value="costs">AI Costs</TabsTrigger>
          <TabsTrigger value="series" onClick={() => navigate('/admin/series-management')}>
            Series Management
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="space-y-6">
          <ModerationStats
            pending={stats.pending}
            total={stats.total}
            today={stats.today}
          />

          <ReportFilters
            status={statusFilter}
            contentType={contentTypeFilter}
            onStatusChange={handleStatusChange}
            onContentTypeChange={handleContentTypeChange}
          />

          {reports.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No reports found</h3>
              <p className="text-muted-foreground">
                {statusFilter === 'pending'
                  ? 'All caught up! No pending reports to review.'
                  : 'No reports match the current filters.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <ReportCard
                  key={report.id}
                  report={report}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tags">
          <TagModerationPanel />
        </TabsContent>

        <TabsContent value="costs">
          <AICostTracker />
        </TabsContent>
      </Tabs>

      <ModerationActionDialog
        report={selectedReport}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedReport(null);
        }}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
